/**
 * Web push (Firebase Cloud Messaging in the browser).
 *
 * Lifecycle:
 *   1. Register `firebase-messaging-sw.js` as a service worker (one-time
 *      per origin).
 *   2. Ask the user for `Notification` permission.
 *   3. Call `firebase.messaging().getToken({ vapidKey, serviceWorkerRegistration })`
 *      to mint a web FCM token. Save to RTDB.
 *   4. Listen for foreground messages via `onMessage`. Background messages
 *      are handled by the service worker.
 *
 * Requires `WebPush.vapidKey` from `app/config.js`. Without it this module
 * logs a warning and exits — native callers are unaffected.
 */

import { auth, ref, getFirebase } from '../../core/firebase.js';
import { WebPush } from '../../app/config.js';
import { LocalState } from '../../core/localState.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('push.web');

let registered = false;
/** @type {ServiceWorkerRegistration | null} */
let swRegistration = null;
/** @type {((notification: any) => void) | null} */
let onMessageHandler = null;

/** @returns {boolean} */
export function isAvailable() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'Notification' in window &&
    !isNative()
  );
}

function isNative() {
  return !!(/** @type {any} */ (window).Capacitor?.isNativePlatform?.());
}

/**
 * @param {{ onMessage?: (notification: any) => void }} [hooks]
 * @returns {Promise<boolean>}
 */
export async function init(hooks = {}) {
  if (registered) return true;
  if (!isAvailable()) {
    log.info('web push unavailable on this platform');
    return false;
  }
  if (!WebPush.vapidKey) {
    log.warn('WebPush.vapidKey is empty — set it in src/app/config.js to enable browser push');
    return false;
  }
  onMessageHandler = hooks.onMessage ?? null;

  try {
    // 1. Register the service worker that handles background messages.
    swRegistration = await navigator.serviceWorker.register(WebPush.serviceWorkerPath, {
      scope: '/',
    });
    log.info('service worker registered:', swRegistration.scope);

    // 2. Permission. On most browsers we can request immediately. On iOS
    //    PWA (standalone), `Notification.requestPermission()` SILENTLY
    //    fails unless called from a user gesture — bootstrap is not one,
    //    so we DEFER the request until the user taps the in-app
    //    "אפשר התראות" banner (see `requestPermissionAndRegister`).
    if (Notification.permission === 'granted') {
      await registerToken();
    } else if (Notification.permission === 'default' && !isiOSStandalone()) {
      // Non-iOS-PWA contexts: request now (bootstrap is fine here).
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await registerToken();
      } else {
        log.info('Notification permission denied by user');
      }
    } else {
      log.info('Notification permission deferred to user gesture (iOS PWA or previously denied)');
    }

    // Foreground messages — wire even if no token yet; firebase.messaging
    // listener is harmless without a registered token.
    //
    // We do NOT call `showNotification` from here anymore. On most browsers
    // the SW's `onBackgroundMessage` fires when the page is backgrounded and
    // `onMessage` here fires when foregrounded — exactly one of them per
    // push. But iOS Safari / standalone PWA can fire BOTH for the same
    // payload, so calling `showNotification` here produces a duplicate the
    // user sees alongside the SW's notification. Skip the foreground
    // display; only update in-app UI via the caller's onMessage hook.
    const firebase = /** @type {any} */ (getFirebase());
    if (typeof firebase.messaging === 'function') {
      const messaging = firebase.messaging();
      messaging.onMessage((/** @type {any} */ payload) => {
        log.info('foreground message:', payload?.data);
        const notif = { ...payload, tapped: false };
        if (onMessageHandler) {
          try { onMessageHandler(notif); }
          catch (e) { log.warn('onMessage threw:', e); }
        }
      });
    }

    registered = true;
    return true;
  } catch (err) {
    log.warn('init failed:', err);
    return false;
  }
}

/**
 * Fetch + persist the FCM token. Called both during `init` (when permission
 * is already granted) and from `requestPermissionAndRegister` (after the
 * user taps the iOS permission button).
 */
async function registerToken() {
  const firebase = /** @type {any} */ (getFirebase());
  if (typeof firebase.messaging !== 'function') {
    log.warn('firebase.messaging not loaded');
    return;
  }
  const messaging = firebase.messaging();
  const token = await messaging.getToken({
    vapidKey: WebPush.vapidKey,
    serviceWorkerRegistration: swRegistration,
  });
  if (token) {
    log.info('web FCM token received');
    await saveToken(token);
  } else {
    log.warn('getToken returned empty — permission may have been denied silently');
  }
}

/**
 * Called from a user-gesture handler (e.g. the "אפשר התראות" banner button).
 * Requests permission + registers the token. iOS PWA REQUIRES this path —
 * calling `requestPermission` outside a user gesture is a silent no-op.
 *
 * @returns {Promise<'granted' | 'denied' | 'unavailable'>}
 */
export async function requestPermissionAndRegister() {
  if (!isAvailable()) return 'unavailable';
  if (!swRegistration) {
    try {
      swRegistration = await navigator.serviceWorker.register(
        WebPush.serviceWorkerPath, { scope: '/' },
      );
    } catch (e) {
      log.warn('SW register failed:', e);
      return 'unavailable';
    }
  }
  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  await registerToken();
  return 'granted';
}

function isiOSStandalone() {
  const ua = navigator.userAgent;
  const isiOS = /iPhone|iPad|iPod/i.test(ua) && !/Android/i.test(ua);
  if (!isiOS) return false;
  const navAny = /** @type {any} */ (navigator);
  if (navAny.standalone === true) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch { return false; }
}

/**
 * @param {string} token
 */
export async function saveToken(token) {
  if (!token) return;
  const uid = auth().currentUser?.uid;
  if (!uid) {
    LocalState.fcmToken = token;
    return;
  }
  LocalState.fcmToken = token;
  try {
    // De-duplicate: register the new web token AND drop any other stale
    // web tokens from this user. FCM rotates tokens between sessions, and
    // without pruning the user accumulates 2-3 tokens per browser →
    // sendPushToTokens fires the same notification once per token, which
    // is what produces the "I got 2 push notifications for one message"
    // bug on Android Chrome.
    //
    // We only prune `platform: 'web'` entries — keep `platform: 'native'`
    // (Capacitor APK) AND legacy unlabelled entries (those might be from
    // a different device entirely; safer to leave them and let FCM's own
    // send-failure cleanup handle them).
    const { db } = await import('../../core/firebase.js');
    const snap = await ref(`users/${uid}/fcmTokens`).once('value');
    const tokens = snap.val() || {};
    /** @type {Record<string, any>} */
    const updates = {};
    for (const [t, meta] of Object.entries(tokens)) {
      if (t === token) continue;
      if (meta && /** @type {any} */ (meta).platform === 'web') {
        updates[`users/${uid}/fcmTokens/${t}`] = null;
      }
    }
    updates[`users/${uid}/fcmTokens/${token}`] = {
      at: Date.now(),
      platform: 'web',
    };
    await db().ref().update(updates);
  } catch (err) {
    log.warn('saveToken failed:', err);
  }
}

/**
 * Browsers don't auto-display foreground push notifications (they only show
 * background ones via the service worker). Render an in-app browser
 * notification for parity with the native behaviour.
 *
 * Uses the same `tag` strategy as `firebase-messaging-sw.js::onBackgroundMessage`
 * (`convId || 'autome-push'`) — so when iOS PWA fires BOTH the SW
 * background handler AND the foreground `onMessage` for the same payload,
 * the second `showNotification` REPLACES the first instead of stacking
 * (which was the source of the "I get 2 push notifications" bug).
 *
 * @param {any} payload
 */
function showForegroundNotification(payload) {
  if (!swRegistration) return;
  const title = payload?.notification?.title ?? payload?.data?.title ?? 'AutoMe';
  const body  = payload?.notification?.body  ?? payload?.data?.body  ?? '';
  if (!body) return;
  try {
    swRegistration.showNotification(title, {
      body,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: payload?.data?.convId || payload?.data?.type || 'autome-push',
      data: payload?.data ?? {},
    });
  } catch (err) {
    log.warn('showNotification failed:', err);
  }
}
